<script setup lang="ts">
	import router from '@/router'
	import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

	import ModalComponent from '@/components/ModalComponent.vue'

	interface Props {
		remaining: number
		expanded?: boolean
		visible?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		expanded: false,
	})

	const calculateInitialRemainingMs = () => {
		const targetTimeMs = props.remaining * 1000
		const currentTimeMs = Date.now()
		const result = Math.max(targetTimeMs - currentTimeMs, 0)

		console.log('calculateInitialRemainingMs:', {
			propsRemaining: props.remaining,
			targetTimeMs: targetTimeMs,
			currentTimeMs: currentTimeMs,
			difference: targetTimeMs - currentTimeMs,
			result: result,
			targetDate: new Date(targetTimeMs).toLocaleString(),
			currentDate: new Date(currentTimeMs).toLocaleString(),
		})

		return result
	}

	const remainingMs = ref(calculateInitialRemainingMs())
	const isExpanded = ref(props.expanded)
	const isVisible = ref(props.visible)
	let intervalId: NodeJS.Timeout | null = null

	const minutes = computed(() =>
		Math.floor((remainingMs.value % (1000 * 60 * 60)) / (1000 * 60))
	)
	const seconds = computed(() =>
		Math.floor((remainingMs.value % (1000 * 60)) / 1000)
	)

	const totalMinutes = computed(() =>
		Math.floor(remainingMs.value / (1000 * 60))
	)

	const toggleExpanded = () => {
		isExpanded.value = !isExpanded.value
	}

	const closeTimer = () => {
		isVisible.value = false
	}

	const isExpired = computed(() => {
		const expired = remainingMs.value <= 0

		return expired
	})

	const updateRemainingMs = () => {
		const targetTimeMs = props.remaining * 1000
		const currentTimeMs = Date.now()

		remainingMs.value = Math.max(targetTimeMs - currentTimeMs, 0)
	}

	let redirectIntervalId: ReturnType<typeof setInterval> | null = null

	onMounted(() => {
		updateRemainingMs()
		intervalId = setInterval(updateRemainingMs, 1000)
	})

	onUnmounted(() => {
		if (intervalId) {
			clearInterval(intervalId)
		}
		if (redirectIntervalId) {
			clearInterval(redirectIntervalId)
		}
	})

	const modalAction = ref(false)

	watch(isExpired, (val) => {
		if (val) {
			modalAction.value = true
		}
	})

	watch(
		() => props.remaining,
		(newRemaining) => {
			remainingMs.value = calculateInitialRemainingMs()

			if (newRemaining > 0) {
				if (redirectIntervalId) {
					clearInterval(redirectIntervalId)
					redirectIntervalId = null
				}
			}
		}
	)
</script>

<template>
	<!-- Reloj flotante -->
	<Teleport to="body">
		<Transition name="float-timer">
			<div
				v-if="isVisible"
				class="fixed top-16 right-3 lg:top-20 lg:right-6 z-50 select-none"
				:class="{ 'animate-pulse': isExpired }"
			>
				<div
					v-if="!isExpanded"
					class="relative group cursor-pointer"
					@click="toggleExpanded"
				>
					<div
						class="w-12 h-12 lg:w-16 lg:h-16 rounded-full border-2 lg:border-4 shadow-lg transition-all duration-300 hover:scale-110"
						:class="
							isExpired ?
								'bg-red-500 border-red-600 hover:bg-red-600'
							:	'bg-[#021F58] border-[#1C3F7A] hover:bg-[#1C3F7A]'
						"
					>
						<div
							class="flex flex-col items-center justify-center h-full text-white"
						>
							<span class="material-symbols-outlined text-sm lg:text-lg"
								>schedule</span
							>
							<div
								class="text-[8px] lg:text-[10px] font-bold leading-none mt-0.5"
							>
								<span v-if="!isExpired">{{ minutes }}m</span>
								<span v-else>!</span>
							</div>
						</div>
					</div>

					<!-- Tooltip hover -->
					<div
						class="hidden lg:block absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none"
					>
						<span v-if="!isExpired"> {{ minutes }}m {{ seconds }}s </span>
						<span v-else>¡Tiempo expirado!</span>
						<div
							class="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900"
						></div>
					</div>
				</div>

				<!-- Versión expandida del reloj -->
				<div
					v-else
					class="bg-white rounded-lg shadow-xl border border-gray-200 p-2 lg:p-4 min-w-[180px] lg:min-w-[280px] max-w-[200px] lg:max-w-[320px]"
				>
					<!-- Header con botones -->
					<div class="flex items-center justify-between mb-2 lg:mb-3">
						<div class="flex items-center gap-1.5 lg:gap-2">
							<span
								class="material-symbols-outlined text-sm lg:text-lg"
								:class="isExpired ? 'text-red-600' : 'text-[#021F58]'"
							>
								schedule
							</span>
							<span
								class="font-semibold text-[10px] lg:text-sm"
								:class="isExpired ? 'text-red-800' : 'text-[#021F58]'"
							>
								Tiempo de reserva
							</span>
						</div>
						<div class="flex items-center gap-1">
							<button
								@click="toggleExpanded"
								class="p-0.5 lg:p-1 hover:bg-gray-100 rounded transition-colors text-sm lg:text-base"
								title="Minimizar"
							>
								-
							</button>
						</div>
					</div>

					<!-- Contenido del tiempo -->
					<div
						v-if="!isExpired"
						class="text-center"
					>
						<!-- Tiempo principal -->
						<div class="grid grid-cols-2 gap-1.5 lg:gap-2 mb-2 lg:mb-3">
							<div class="bg-[#021F58]/8 rounded-lg p-1.5 lg:p-2">
								<div class="text-base lg:text-xl font-bold text-[#021F58]">
									{{ minutes }}
								</div>
								<div class="text-[9px] lg:text-xs text-[#021F58]/60">min</div>
							</div>
							<div class="bg-[#021F58]/8 rounded-lg p-1.5 lg:p-2">
								<div class="text-base lg:text-xl font-bold text-[#021F58]">
									{{ seconds }}
								</div>
								<div class="text-[9px] lg:text-xs text-[#021F58]/60">seg</div>
							</div>
						</div>

						<!-- Información adicional -->
						<div class="text-[9px] lg:text-xs text-gray-600 font-bold">
							Total: {{ totalMinutes.toLocaleString() }} min
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>

	<ModalComponent :model-value="modalAction">
		<template #modalBody>
			<div class="p-5 flex flex-col items-center">
				<p>El tiempo para concluir la reserva ha terminado</p>
				<p>Será redirigido para seleccionar una tarifa</p>

				<button
					@click="
						() => {
							router.back()
							modalAction = false
						}
					"
					class="mt-4 bg-[#021F58] text-white px-4 py-2 rounded-md hover:bg-[#1C3F7A] transition-colors flex items-center gap-2"
				>
					Regresar a la página anterior
				</button>
			</div>
		</template>
	</ModalComponent>
</template>

<style scoped>
	.float-timer-enter-active,
	.float-timer-leave-active {
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.float-timer-enter-from {
		opacity: 0;
		transform: translateY(-20px) scale(0.9);
	}

	.float-timer-leave-to {
		opacity: 0;
		transform: translateY(-20px) scale(0.9);
	}

	@keyframes pulse {
		0%,
		100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.05);
		}
	}

	.animate-pulse {
		animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
	}

	/* Efecto de hover mejorado */
	.group:hover .group-hover\:opacity-100 {
		opacity: 1;
	}

	.transition-all {
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 300ms;
	}
</style>
